import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./GiftCardsSection.scss";
import { Button } from "../../../elements/button/Button";
import { SvgIcon } from "../../../elements/svg-icon/svg-icon";
import { COLORS } from "../../../models/colors";
import { VARIANTS } from "../../../models/variants";
import { bemElement, bemModifier } from "../../../utils/bem-class-names";
import { useAxios } from "../../../providers/axios";

// Gift cards this customer BOUGHT, on the account page next to Credits.
//
// Cards they were GIVEN are deliberately absent: the only thing tying someone
// to a received card is a recipient email typed at a front desk, and product
// settled that a card is not attached to an account on that basis. Nothing is
// lost - a card is redeemed by code, and the code is in the gift email.
//
// The job this screen does is answer "what was the code again?" for the buyer,
// who is the person the recipient asks when the email goes missing. That is why
// the code is shown in full rather than masked.

const baseClassName = "gift-cards-section";
const bem = bemElement(baseClassName);

type MyGiftCard = {
  id: string;
  formatted_code: string;
  balance: number;
  initial_value: number;
  status: "ACTIVE" | "FULLY_REDEEMED" | "EXPIRED" | "DISABLED";
  issued_at: string;
  recipient_email: string | null;
  is_for_someone_else?: boolean;
  company_name: string | null;
  company_slug: string | null;
};

const statusLabels: Record<string, string> = {
  ACTIVE: "Active",
  FULLY_REDEEMED: "Fully redeemed",
  EXPIRED: "Expired",
  DISABLED: "Disabled"
};

const GiftCardsSection = () => {
  const { api } = useAxios();
  const navigate = useNavigate();
  const [cards, setCards] = useState<MyGiftCard[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    api
      .get("/v1/gift-cards/mine")
      .then((response: { data: { data?: MyGiftCard[] } }) => {
        if (!cancelled) setCards(response.data?.data || []);
      })
      .catch(() => {
        // A failure here shows as "none yet" rather than an error state. There
        // is nothing the customer can do about it, and the account page should
        // not break around it.
        if (!cancelled) setCards([]);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  if (cards === null) {
    return <div className={baseClassName} />;
  }

  if (cards.length === 0) {
    return (
      <div className={baseClassName}>
        <div className={bem("empty")}>
          <SvgIcon name="gift_card" className={bem("empty-icon")} />
          <div className={bem("empty-title")}>No gift cards yet</div>
          <div className={bem("empty-caption")}>
            Gift cards you buy will appear here with their codes.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={baseClassName}>
      <div className={bem("title mt-4 lg:mt-0")}>
        These are the gift cards you&apos;ve purchased. Share the gift card code
        with friends or anyone you&apos;d like to gift it to.
      </div>
      <div className={bem("list")}>
        {cards.map((card, i) => {
          const forSomeoneElse =
            card.is_for_someone_else ?? Boolean(card.recipient_email);
          return (
            <div className={bem("card-cont")} key={card.id}>
              <div className={bem("card")}>
                <div className={bem("icon")}>
                  <SvgIcon name="gift_card" />
                </div>
                <div className={bem("details")}>
                  {/*
                  Status sits up here with the salon rather than in the figures
                  below. The account column is narrow, and a chip on the same
                  line as the balance pushed it onto a row of its own.
                */}
                  <div className={bem("heading")}>
                    <span className={bem("salon")}>
                      {card.company_name || "Gift card"}
                    </span>
                    <span
                      className={bemModifier(
                        bem("status"),
                        card.status.toLowerCase()
                      )}
                    >
                      {statusLabels[card.status] || card.status}
                    </span>
                  </div>
                  <div className={bem("code")}>{card.formatted_code}</div>
                  {!forSomeoneElse && (
                    <div className={bem("meta")}>
                      <span className={bem("balance")}>
                        ${card.balance.toFixed(2)}
                      </span>
                      <span>left of ${card.initial_value.toFixed(2)}</span>
                    </div>
                  )}
                  {forSomeoneElse && card.recipient_email && (
                    <div className={bem("recipient")}>
                      Sent to {card.recipient_email}
                    </div>
                  )}
                </div>
                {/*
                Only offered while there is something left to spend, and only
                when we know where to send them.
              */}
                {!forSomeoneElse &&
                  card.company_slug &&
                  card.status === "ACTIVE" && (
                    <Button
                      className={bem("book")}
                      variant={VARIANTS.OUTLINED}
                      color={COLORS.SECONDARY}
                      text="Book"
                      iconRightName="arrow_right"
                      onClick={() => navigate(`/company/${card.company_slug}`)}
                    />
                  )}
              </div>
              {i !== cards.length - 1 && <div className={bem("divider")} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GiftCardsSection;
